import { useRef, useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Send, Sparkles, FileText, Bot, Wrench, CheckCircle2, Coins } from "lucide-react";

const TOOL_LABELS = {
  query_flavor_graph: "consultou o grafo",
  calculate_pairing_score: "calculou sinergia",
  find_chemical_substitutes: "buscou substitutos",
  search_scientific_library: "consultou o acervo",
};

const md = {
  h1: (p) => <h3 className="text-base font-semibold text-gray-100 mt-2 mb-1" {...p} />,
  h2: (p) => <h4 className="text-sm font-semibold text-gray-100 mt-2 mb-1" {...p} />,
  h3: (p) => <h4 className="text-sm font-semibold text-gray-200 mt-2 mb-1" {...p} />,
  p: (p) => <p className="leading-relaxed my-1.5" {...p} />,
  ul: (p) => <ul className="list-disc pl-5 my-1.5 space-y-0.5" {...p} />,
  ol: (p) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5" {...p} />,
  strong: (p) => <strong className="text-white font-semibold" {...p} />,
  code: ({ inline, ...p }) =>
    inline ? (
      <code className="font-mono text-xs text-orange-300 bg-orange-950/40 px-1.5 py-0.5 rounded border border-orange-800/50" {...p} />
    ) : (
      <code className="block bg-[#0B0F17] p-3 rounded-md border border-orange-500/30 text-orange-300 font-mono text-xs my-2 overflow-x-auto" {...p} />
    ),
  table: (p) => <table className="my-2 w-full text-xs border border-[#2A364F] rounded overflow-hidden" {...p} />,
  th: (p) => <th className="bg-[#0B0F17] border border-[#2A364F] px-2 py-1 text-left text-slate-300" {...p} />,
  td: (p) => <td className="border border-[#2A364F] px-2 py-1 text-slate-300" {...p} />,
  a: (p) => <a className="text-[#00E5FF] underline" target="_blank" rel="noreferrer" {...p} />,
};

function SyncBadge({ msg }) {
  if (!msg.autoSynced) return null;
  const cost = msg.costEstimate;
  return (
    <div className="bg-[#1F293D] p-2.5 rounded-xl border border-emerald-500/30 flex items-center justify-between gap-3 mt-3" data-testid="auto-sync-badge">
      <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400">
        <CheckCircle2 className="h-3.5 w-3.5" /> Sincronizado automaticamente
      </span>
      <div className="flex items-center gap-2 text-[11px]">
        {typeof msg.synergyIndex === "number" && (
          <Badge variant="outline" className="border-[#E05A2B]/40 text-[#E05A2B]">
            Sinergia {Math.round(msg.synergyIndex * 100)}%
          </Badge>
        )}
        {cost && (
          <span className="inline-flex items-center gap-1 text-slate-300" title={cost.note}>
            <Coins className="h-3 w-3 text-[#FACC15]" /> ~{cost.currency} {cost.total_cost}
          </span>
        )}
      </div>
    </div>
  );
}

export default function ChatView({ souschef, onEntityClick, canExport = false, compact = false, placeholder }) {
  const { messages, busy, send } = souschef;
  const [q, setQ] = useState("");
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const submit = () => { if (q.trim() && !busy) { send(q); setQ(""); } };

  return (
    <div className="flex flex-col h-full" data-testid="chat-view">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={
                m.role === "user"
                  ? "bg-[#1F293D] text-gray-100 border border-[#2A364F] rounded-2xl rounded-tr-sm p-3.5 ml-auto max-w-[80%] text-sm"
                  : m.error
                  ? "bg-[#FF007A]/15 text-[#FF007A] border border-[#FF007A]/40 rounded-2xl p-4 mr-auto max-w-[90%] text-sm"
                  : "bg-[#161E2E] text-gray-200 border border-[#2A364F] rounded-2xl rounded-tl-sm p-4 mr-auto max-w-[92%] shadow-lg text-sm"
              }
            >
              {m.role === "assistant" && !m.error && (
                <div className="flex items-center gap-1.5 mb-1.5 text-[#E05A2B]">
                  <Bot className="h-3.5 w-3.5" />
                  <span className="text-[11px] uppercase tracking-wider">SousChef AI</span>
                </div>
              )}

              {m.tools?.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5" data-testid="souschef-tools">
                  {m.tools.map((t, idx) => (
                    <span key={idx} className="inline-flex items-center gap-1 rounded-md bg-[#0B0F17] border border-[#2A364F] text-[10px] text-slate-400 px-1.5 py-0.5">
                      <Wrench className="h-2.5 w-2.5 text-[#00E5FF]" />{TOOL_LABELS[t] || t}
                    </span>
                  ))}
                </div>
              )}

              {m.role === "assistant" ? (
                <div className="prose-invert text-sm text-gray-200">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{m.text || ""}</ReactMarkdown>
                </div>
              ) : (
                <p className="whitespace-pre-wrap leading-relaxed">{m.text}</p>
              )}

              {m.entities?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {m.entities.map((e) => (
                    <button
                      key={e}
                      data-testid={`rag-entity-${e}`}
                      onClick={() => onEntityClick && onEntityClick(e)}
                      className="rounded-full bg-[#E05A2B]/15 text-[#E05A2B] px-2.5 py-1 text-xs hover:bg-[#E05A2B]/30 transition-colors"
                    >
                      <Sparkles className="inline h-3 w-3 mr-1" />{e}
                    </button>
                  ))}
                </div>
              )}

              {m.sources?.length > 0 && (
                <div className="mt-3 space-y-1.5 border-t border-[#2A364F] pt-2">
                  {m.sources.map((s) => (
                    <div key={s.marker} className="flex items-start gap-2 text-xs text-slate-400">
                      <FileText className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[#00E5FF]" />
                      <span>
                        <b className="text-[#00E5FF]">[{s.marker}]</b> {s.title || s.source || "fonte"}
                        <Badge variant="outline" className="ml-2 border-[#2A364F] text-slate-500">
                          {Math.round((s.score || 0) * 100)}%
                        </Badge>
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {m.role === "assistant" && !m.error && <SyncBadge msg={m} />}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start" data-testid="souschef-thinking">
            <div className="rounded-2xl px-4 py-3 text-sm bg-[#161E2E] border border-[#2A364F] text-slate-400">
              <span className="inline-flex items-center gap-2"><Bot className="h-3.5 w-3.5 text-[#E05A2B] animate-pulse" /> analisando o grafo e o acervo…</span>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-[#2A364F] p-3 flex gap-2">
        <Textarea
          data-testid="rag-chat-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder={placeholder || "Ex: Calcule a sinergia entre café e chocolate."}
          className={`${compact ? "min-h-[44px] max-h-24" : "min-h-[52px] max-h-40"} resize-none bg-[#0B0F17] border-[#2A364F]`}
        />
        <Button data-testid="rag-chat-send" onClick={submit} disabled={busy} className="bg-[#E05A2B] text-white hover:bg-[#F06B3C] self-end">
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
